/**
 * EndScreen - Shown at the end of the match with detailed stats and restart.
 * Covers tasks 3.5 (detailed match stats), 5.15 (leaderboard display),
 * 5.11 (achievements display).
 */

import * as GUI from "babylonjs-gui";
import { GameScore } from "../systems/GameState";
import { MatchStats } from "../systems/MatchStatsSystem";
import { SaveSystem } from "../systems/SaveSystem";

export class EndScreen {
  private _texture: GUI.AdvancedDynamicTexture;
  private _container: GUI.Rectangle;
  private _resultText: GUI.TextBlock;
  private _scoreText: GUI.TextBlock;
  private _statsPanel: GUI.StackPanel;
  private _achievementText: GUI.TextBlock;

  constructor(onRestart: () => void) {
    this._texture = GUI.AdvancedDynamicTexture.CreateFullscreenUI("endScreen");

    this._container = new GUI.Rectangle("endBg");
    this._container.width = "100%";
    this._container.height = "100%";
    this._container.thickness = 0;
    this._container.background = "rgba(0,0,0,0.82)";
    this._container.isVisible = false;
    this._texture.addControl(this._container);

    // Result heading
    this._resultText = new GUI.TextBlock("result", "");
    this._resultText.color = "white";
    this._resultText.fontSize = 72;
    this._resultText.fontWeight = "900";
    this._resultText.fontFamily = "Orbitron, sans-serif";
    this._resultText.outlineWidth = 4;
    this._resultText.outlineColor = "black";
    this._resultText.top = "-230px";
    this._container.addControl(this._resultText);

    // Score
    this._scoreText = new GUI.TextBlock("endScore", "");
    this._scoreText.color = "white";
    this._scoreText.fontSize = 42;
    this._scoreText.fontWeight = "700";
    this._scoreText.fontFamily = "Orbitron, sans-serif";
    this._scoreText.top = "-155px";
    this._container.addControl(this._scoreText);

    // Stats panel header
    const statsHeader = new GUI.TextBlock("statsHeader", "— MATCH STATISTICS —");
    statsHeader.color = "#aaaaaa";
    statsHeader.fontSize = 12;
    statsHeader.fontWeight = "700";
    statsHeader.fontFamily = "Orbitron, sans-serif";
    statsHeader.top = "-100px";
    this._container.addControl(statsHeader);

    // Stats panel
    this._statsPanel = new GUI.StackPanel("statsPanel");
    this._statsPanel.width = "380px";
    this._statsPanel.height = "180px";
    this._statsPanel.top = "-10px";
    this._statsPanel.isVertical = true;
    this._container.addControl(this._statsPanel);

    // Achievement notification
    this._achievementText = new GUI.TextBlock("achievementText", "");
    this._achievementText.color = "#ffcc00";
    this._achievementText.fontSize = 13;
    this._achievementText.fontWeight = "700";
    this._achievementText.fontFamily = "Orbitron, sans-serif";
    this._achievementText.top = "130px";
    this._achievementText.isVisible = false;
    this._container.addControl(this._achievementText);

    // Career Stats Button
    const careerBtn = GUI.Button.CreateSimpleButton("careerBtn", "VIEW CAREER STATS");
    careerBtn.width = "220px";
    careerBtn.height = "40px";
    careerBtn.color = "#aaaaaa";
    careerBtn.cornerRadius = 3;
    careerBtn.thickness = 1;
    careerBtn.background = "rgba(40,40,40,0.8)";
    careerBtn.fontSize = 12;
    careerBtn.fontWeight = "700";
    careerBtn.fontFamily = "Orbitron, sans-serif";
    careerBtn.top = "155px";
    careerBtn.onPointerUpObservable.add(() => {
      this._showCareerStats();
    });
    this._container.addControl(careerBtn);

    // Restart button
    const btn = GUI.Button.CreateSimpleButton("restartBtn", "REMATCH");
    btn.width = "280px";
    btn.height = "65px";
    btn.color = "#00ff88";
    btn.cornerRadius = 4;
    btn.thickness = 1;
    btn.background = "rgba(17, 85, 204, 0.8)";
    btn.fontSize = 22;
    btn.fontWeight = "700";
    btn.fontFamily = "Orbitron, sans-serif";
    btn.top = "210px";

    btn.onPointerDownObservable.add(() => {
      btn.background = "rgba(13, 68, 153, 0.9)";
      btn.scaleX = 0.98;
      btn.scaleY = 0.98;
    });
    btn.onPointerUpObservable.add(() => {
      btn.background = "rgba(17, 85, 204, 0.8)";
      btn.scaleX = 1;
      btn.scaleY = 1;
      onRestart();
    });
    btn.onPointerEnterObservable.add(() => {
      btn.thickness = 3;
      btn.background = "rgba(25, 100, 230, 1)";
    });
    btn.onPointerOutObservable.add(() => {
      btn.thickness = 1;
      btn.background = "rgba(17, 85, 204, 0.8)";
    });

    this._container.addControl(btn);
  }

  show(score: GameScore, stats?: MatchStats, difficulty?: string): void {
    let result: string;
    let resultColor: string;

    if (score.player > score.opponent) {
      result = "MATCH WON";
      resultColor = "#00ff88";
    } else if (score.player < score.opponent) {
      result = "DEFEATED";
      resultColor = "#ff3366";
    } else {
      result = "DRAW";
      resultColor = "#ffcc00";
    }

    this._resultText.text = result;
    this._resultText.color = resultColor;
    this._scoreText.text = `${score.player} - ${score.opponent}`;

    // Populate stats
    this._statsPanel.clearControls();
    if (stats) {
      const matchResult = score.player > score.opponent ? "win" :
        score.player < score.opponent ? "loss" : "draw";

      const possession = Math.round(
        (stats.playerPossessionTime /
          Math.max(0.1, stats.playerPossessionTime + stats.opponentPossessionTime)) * 100
      );

      const statItems = [
        { label: "POSSESSION", player: `${possession}%`, opponent: `${100 - possession}%` },
        { label: "SHOTS ON TARGET", player: `${stats.playerShotsOnTarget}`, opponent: `${stats.opponentShotsOnTarget}` },
        { label: "TOTAL SHOTS", player: `${stats.playerShots}`, opponent: `${stats.opponentShots}` },
        { label: "CORNERS", player: `${stats.playerCorners}`, opponent: `${stats.opponentCorners}` },
        { label: "FOULS", player: `${stats.playerFouls}`, opponent: `${stats.opponentFouls}` },
      ];

      statItems.forEach(item => {
        this._addStatRow(item.label, item.player, item.opponent);
      });

      // Save match record
      const record = {
        date: new Date().toLocaleDateString(),
        playerScore: score.player,
        opponentScore: score.opponent,
        duration: stats.totalTime,
        difficulty: difficulty || "EASY",
        result: matchResult as "win" | "loss" | "draw",
        shotsOnTarget: stats.playerShotsOnTarget,
        possession,
      };
      SaveSystem.recordMatch(record);

      // Check achievements
      const newAchievements: string[] = [];
      const achievementMap: Record<string, string> = {
        first_win: "🏆 Achievement: First Victory!",
        hat_trick: "🎯 Achievement: Hat Trick Hero!",
        clean_sheet: "🛡️ Achievement: Clean Sheet!",
        veteran: "⚽ Achievement: Veteran Player!",
        legend: "👑 Achievement: Living Legend!",
        goal_machine: "💥 Achievement: Goal Machine!",
      };

      for (const [id, label] of Object.entries(achievementMap)) {
        const isNew = SaveSystem.unlockAchievement(id);
        if (isNew) newAchievements.push(label);
      }

      if (newAchievements.length > 0) {
        this._achievementText.text = newAchievements.join(" • ");
        this._achievementText.isVisible = true;
      }

      // Add to leaderboard if won
      if (matchResult === "win") {
        const stats2 = SaveSystem.getStats();
        SaveSystem.addLeaderboardEntry("Player", score.player, stats2.wins);
      }
    }

    this._container.isVisible = true;
  }

  private _addStatRow(label: string, playerVal: string, opponentVal: string): void {
    const row = new GUI.StackPanel(`stat_${label}`);
    row.isVertical = false;
    row.height = "28px";
    row.width = "380px";
    this._statsPanel.addControl(row);

    const pText = new GUI.TextBlock(`p_${label}`, playerVal);
    pText.color = "#88aaff";
    pText.fontSize = 12;
    pText.fontWeight = "700";
    pText.fontFamily = "Orbitron, sans-serif";
    pText.width = "80px";
    pText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    row.addControl(pText);

    const lText = new GUI.TextBlock(`l_${label}`, label);
    lText.color = "#888888";
    lText.fontSize = 10;
    lText.fontFamily = "Orbitron, sans-serif";
    lText.width = "200px";
    lText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    row.addControl(lText);

    const oText = new GUI.TextBlock(`o_${label}`, opponentVal);
    oText.color = "#ff8888";
    oText.fontSize = 12;
    oText.fontWeight = "700";
    oText.fontFamily = "Orbitron, sans-serif";
    oText.width = "80px";
    oText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    row.addControl(oText);
  }

  private _showCareerStats(): void {
    const stats = SaveSystem.getStats();
    // Clear and rebuild panel with career stats
    this._statsPanel.clearControls();
    this._achievementText.isVisible = false;

    const careerItems = [
      { label: "GAMES PLAYED", player: `${stats.gamesPlayed}`, opponent: "" },
      { label: "WINS", player: `${stats.wins}`, opponent: "" },
      { label: "LOSSES", player: `${stats.losses}`, opponent: "" },
      { label: "DRAWS", player: `${stats.draws}`, opponent: "" },
      { label: "GOALS SCORED", player: `${stats.goalsScored}`, opponent: "" },
      { label: "BEST SCORE", player: `${stats.highestScore}`, opponent: "" },
    ];
    careerItems.forEach(item => {
      this._addStatRow(item.label, item.player, item.opponent);
    });
  }

  hide(): void {
    this._container.isVisible = false;
    this._achievementText.isVisible = false;
  }

  dispose(): void {
    this._texture.dispose();
  }
}
